import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MultiValue, SingleValue } from 'react-select';

import { FormInputPopup } from '@components/form-input-popup/form-input-popup';
import { Option } from '@components/form-select/form-select';
import {
	AdditionalType,
	  FormSelectAsyncPagination,
} from '@components/form-select/form-select-async-pagination';
import { Field, FormikValues, useFormikContext } from 'formik';
import { getCurrentUserService } from 'src/current-user/infrastructure/getters';
import { calculateOffset, userHasRoles } from 'src/helpers/app-utils';
import { getStandardService } from 'src/standard/infrastructure/getters';
import { SystemRole } from 'src/user/domain/system-role.entity';

const limit = 6;

export default function CreateNewStandardEAReferences() {
	const url = window.location.href;
	const { t } = useTranslation();
	const { setFieldValue } = useFormikContext<FormikValues>();

	const currentUserService = getCurrentUserService();
	const standardService = getStandardService();

	const handleLoadConsideredStandards = async (
		search: string,
		_prevOptions: any,
		{ page }: AdditionalType
	) => {
		const offset = calculateOffset(page, limit);
		const count = await standardService.getLatestStandardsList(limit, offset, {
			search: search ? search : undefined,
		});
		const consideredStandards = standardService.latestStandards
			.filter(
				standard =>
					standard.registrationNumber !== standardService.singleStandard?.registrationNumber
			)
			.map(standard => {
				return {
					label: `${standard.registrationNumber ?? ''} ${standard.form ?? ''} ${
						standard.localizedTitle ?? ''
					} ${standard.edition}`,
					value: standard.id,
				};
			});
		const hasMore = page < Math.ceil(count / limit);
		return {
			options: consideredStandards,
			hasMore,
			additional: {
				page: page + 1,
			},
		};
	};

	useEffect(() => {
		const onLoadPage = async () => {
			await standardService.getStandardList(20, 0, false);
		};

		onLoadPage();
	}, [standardService]);

	const handleChange = async (name: string, option: MultiValue<Option> | SingleValue<Option>) => {
		if (!option || !Array.isArray(option)) {
			return;
		}

		const newOption = option.map(item => {
			const splitLabel = item.label.split(' ');
			return {
				label: `${splitLabel[0]} ${splitLabel[1]} ${splitLabel[splitLabel.length - 1]}`,
				value: item.value,
			};
		});

		await setFieldValue(name, newOption);
	};

	return (
		<>
			<Field
				component={FormSelectAsyncPagination}
				name="consideredStandards"
				title={t('standard.createNWP.justification.consideredStandards')}
				fullWidth
				disabled={userHasRoles([SystemRole.EXPERT], currentUserService.userRoles!)}
				isMulti
				showCheckboxOption
				loadOptions={handleLoadConsideredStandards}
				closeMenuOnSelect={false}
				hideSelectedOptions={false}
				onChange={handleChange}
				useSubmitOnChange
			/>
			<Field
				component={FormInputPopup}
				name="externalConsideredStandards"
				title={t('standard.createNWP.justification.externalConsideredStandards')}
				fullWidth
				showError
				enableCopy
				maxLength={500}
				useSubmitOnBlur
				disableMaxLength={url.includes('draft')}
			/>
		</>
	);
}
